import { Component } from '@theme/component';

/** Same threshold as `video-reviews.js` setupVideoObserver */
const VIDEO_PLAY_VISIBILITY_THRESHOLD = 0.3;

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #sourcesHydrated = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    if (this.hasAttribute('data-play-on-visible')) {
      this.#setupPlayOnVisible(videoSources, videoElement);
      return;
    }

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }

  disconnectedCallback() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    super.disconnectedCallback();
  }

  /**
   * Defer setting `<source src>` and `load()` until intersecting; play/pause like video-reviews carousel.
   * @param {HTMLElement[]} videoSources
   * @param {HTMLVideoElement} videoElement
   */
  #setupPlayOnVisible(videoSources, videoElement) {
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = videoElement;
          if (entry.isIntersecting) {
            if (!this.#sourcesHydrated) {
              for (const source of videoSources) {
                const { videoSource } = source.dataset;
                if (videoSource) source.setAttribute('src', videoSource);
              }
              this.#sourcesHydrated = true;
              video.load();
            }
            video.muted = true;
            video.setAttribute('muted', '');
            video.setAttribute('playsinline', '');

            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                video.load();
              });
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: VIDEO_PLAY_VISIBILITY_THRESHOLD },
    );

    this.#visibilityObserver.observe(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
